/**
 * ColorField — native color picker for the node-form `color` columns.
 *
 * Implements the storage contract in the roadmap §1.2 (verified against the VR
 * client + live DB). The existing parseHexColor/getHexColor helpers CANNOT be
 * reused raw: they assume a leading "#" on input and always emit one on output,
 * but the VR client writes/reads bare 8-hex `RRGGBBAA` (ColorUtility) and
 * double-prefixes ("##…") if a stored value already carries a "#". So we
 * normalize here instead.
 *
 * Read  : tolerate the three formats seen in prod — `RRGGBBAA` (dominant),
 *         bare 6-hex `RRGGBB` (the schema default "FFFFFF"), and rogue
 *         `#RRGGBB`. Feed the first 6 hex digits to <input type="color">
 *         (its only accepted format, `#rrggbb`) and remember the alpha byte.
 * Write : emit UPPERCASE 8-hex `RRGGBBAA`, NO "#", re-appending the remembered
 *         alpha byte. Alpha *editing* is deferred; alpha *preservation* is
 *         mandatory (else every web edit strips live transparency).
 */

const HEX_RE = /[^0-9a-fA-F]/g;

/** Strip a leading "#" and any non-hex noise, returning up to 8 hex chars. */
const cleanHex = (raw: string): string => (raw ?? "").replace(/^#/, "").replace(HEX_RE, "");

export interface NormalizedColor {
  /** `#rrggbb` — the only format the native <input type="color"> accepts. */
  pickerHex: string;
  /** Two-hex uppercase alpha byte, preserved across edits ("FF" when absent). */
  alpha: string;
}

/** Read/normalize any of the three stored formats into picker-ready parts. */
export const normalizeStoredColor = (stored?: string): NormalizedColor => {
  const hex = cleanHex(stored ?? "");
  const rgb = hex.slice(0, 6).padEnd(6, "0").toLowerCase();
  const alpha = hex.length >= 8 ? hex.slice(6, 8).toUpperCase() : "FF";
  return { pickerHex: `#${rgb}`, alpha };
};

/** Write: UPPERCASE 8-hex `RRGGBBAA`, no "#", with the preserved alpha byte. */
export const composeStoredColor = (pickerHex: string, alpha: string): string => {
  const rgb = cleanHex(pickerHex).slice(0, 6).padEnd(6, "0").toUpperCase();
  const a = (alpha || "FF").toUpperCase();
  return `${rgb}${a}`;
};

const ColorField = ({
  label,
  value,
  onChange,
  disabled = false,
  className = undefined,
}: {
  label?: string;
  value?: string;
  onChange: (stored: string) => void;
  disabled?: boolean;
  className?: string;
}) => {
  const { pickerHex, alpha } = normalizeStoredColor(value);

  return (
    <div className="w-full">
      {label ? <label className="block text-sm font-medium text-gray-700">{label}</label> : null}
      <div className="mt-1 flex items-center gap-3">
        <input
          type="color"
          value={pickerHex}
          disabled={disabled}
          onChange={(e) => onChange(composeStoredColor(e.target.value, alpha))}
          className={`${
            className ?? ""
          } h-9 w-12 cursor-pointer rounded-md border border-gray-300 p-0.5 disabled:cursor-not-allowed`}
        />
        <span className="text-sm text-gray-500 uppercase">
          {composeStoredColor(pickerHex, alpha)}
        </span>
      </div>
    </div>
  );
};

export default ColorField;
